import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PageProps } from './types';
import { Button, EmptyState, KindBadge, WaveLane } from '../components';
import { getSegmentsByTask, getTaskAudio } from '../db';
import type { Segment, SegmentKind, Task } from '../types';
import {
  datasetFileName,
  buildDatasetFrom,
  downloadJson,
  exportTaskDataset,
  finalKind,
  finalWord,
  listDoneTasks,
  transcriptOf,
  wasCorrected,
} from '../services/reconcile';

/**
 * Done tab.
 *
 * List of completed tasks -> open one -> karaoke view: the ORIGINAL audio plays
 * while each segment's final label is highlighted in sync with its timestamps.
 * Tap any word to seek there. "Export JSON" downloads the dataset.
 *
 * The `lf-` prefixed classes this page uses live in `src/styles.css`.
 */
export default function CompletedPage({ onGoToTab, focusTaskId }: PageProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Opened transcript. Seeded from the shell's hand-off ("open THIS one"). */
  const [openId, setOpenId] = useState<string | null>(focusTaskId);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setTasks(await listDoneTasks());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load completed tasks.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const openTask = useMemo(() => tasks.find((t) => t.id === openId) ?? null, [tasks, openId]);

  // a task that got re-opened elsewhere disappears from the list — bail out
  useEffect(() => {
    if (openId && !loading && !openTask) setOpenId(null);
  }, [openId, openTask, loading]);

  return (
    <>
      {openTask ? (
        <TaskKaraoke key={openTask.id} task={openTask} onBack={() => setOpenId(null)} />
      ) : (
        <>
          <header className="page__head">
            <h1 className="page__title">Done</h1>
            <span className="page__sub">{tasks.length ? `${tasks.length} completed` : 'transcript & export'}</span>
          </header>

          {error && <p className="lf-error">{error}</p>}

          {loading ? (
            <p className="lf-muted">Loading…</p>
          ) : tasks.length === 0 ? (
            <EmptyState
              icon="📦"
              title="No completed tasks"
              hint="Tasks land here once every segment is verified or corrected."
              action={
                <Button variant="primary" onClick={() => onGoToTab('tasks')}>
                  Go to Tasks
                </Button>
              }
            />
          ) : (
            <div className="col">
              {tasks.map((task) => (
                <DoneTaskCard key={task.id} task={task} onOpen={() => setOpenId(task.id)} />
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* List card                                                           */
/* ------------------------------------------------------------------ */

function DoneTaskCard({ task, onOpen }: { task: Task; onOpen: () => void }) {
  const [exporting, setExporting] = useState(false);

  const onExport = async () => {
    setExporting(true);
    try {
      await exportTaskDataset(task.id);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="card">
      <div className="lf-card-title">{task.name}</div>
      <div className="lf-meta mono">
        {formatDate(task.createdAt)} · {formatTime(task.durationSec)}
      </div>
      <div className="row lf-card-actions">
        <Button variant="primary" size="sm" onClick={onOpen}>
          Open
        </Button>
        <Button size="sm" onClick={() => void onExport()} disabled={exporting}>
          {exporting ? 'Exporting…' : 'Export JSON'}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Karaoke view                                                        */
/* ------------------------------------------------------------------ */

function TaskKaraoke({ task, onBack }: { task: Task; onBack: () => void }) {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  /**
   * React nulls `audioRef` when it detaches the <audio> node, and it does that
   * *before* this component's effect cleanups run — so teardown code that reads
   * `audioRef.current` finds nothing and silently does nothing. This ref only
   * ever takes non-null nodes, so the cleanup below can still reach the element.
   */
  const audioNodeRef = useRef<HTMLAudioElement | null>(null);
  const setAudioNode = useCallback((node: HTMLAudioElement | null) => {
    audioRef.current = node;
    if (node) audioNodeRef.current = node;
  }, []);
  const rafRef = useRef<number | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef<HTMLButtonElement | null>(null);

  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(task.durationSec || 0);
  const [isPlaying, setIsPlaying] = useState(false);

  // load segments + original audio
  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;

    setLoading(true);
    // TODO(partition) — F3: `getSegmentsByTask` returns the FULL row set, with
    // no active-leaf filter. Every queue read in `src/db/queues.ts` filters with
    // `activeLeaves` (`!splitAt && !obsoleteAt`); this one does not.
    //
    // Consequence on a RECUT task: `applyRecut` supersedes its inputs by
    // stamping `obsoleteAt` rather than deleting them, so the superseded rows
    // are still here. The karaoke transcript below renders them alongside their
    // replacements, and — because the old and new rows cover the same audio —
    // the Export JSON emits OVERLAPPING spans. A task that was never recut is
    // unaffected, which is why this is easy to miss.
    //
    // The fix is `activeLeaves(await getSegmentsByTask(task.id))` from
    // `../types`, but it CHANGES WHAT EXPORT EMITS, so it is out of scope for a
    // zero-behavior-change pass. See ARCHITECTURE.md, follow-up F3 — it needs
    // its own verified pass alongside `reconcile.isTaskResolved`,
    // `reconcile.buildDataset` and `reconcile.buildExportPayload`, which have
    // the same gap.
    Promise.all([getSegmentsByTask(task.id), getTaskAudio(task.id)])
      .then(([segs, blob]) => {
        if (cancelled) return;
        setSegments(segs);
        if (blob) {
          url = URL.createObjectURL(blob);
          setAudioUrl(url);
          setError(null);
        } else {
          setAudioUrl(null);
          setError('Original audio is missing for this task — transcript only.');
        }
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load this task.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
      // React just detaches the <audio> node. Chrome pauses a media element on
      // removal, but iOS Safari does not — it kept the recording playing with
      // nothing on screen to stop it. Detach the source before revoking it too.
      const el = audioNodeRef.current;
      if (el) {
        el.pause();
        el.removeAttribute('src');
        el.load();
      }
      if (url) URL.revokeObjectURL(url);
    };
  }, [task.id]);

  // drive the highlight from a rAF loop while playing (timeupdate is too coarse)
  const stopLoop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const startLoop = useCallback(() => {
    stopLoop();
    const tick = () => {
      const el = audioRef.current;
      if (el) setTime(el.currentTime);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [stopLoop]);

  useEffect(() => stopLoop, [stopLoop]);

  const activeIndex = useMemo(() => findActiveIndex(segments, time), [segments, time]);
  const passedIndex = useMemo(() => findLastStartedIndex(segments, time), [segments, time]);

  // keep the highlighted chip in view inside the transcript box
  useEffect(() => {
    if (activeIndex < 0) return;
    activeRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [activeIndex]);

  const seekTo = useCallback((seconds: number, autoplay: boolean) => {
    const el = audioRef.current;
    if (!el) return;
    // nudge past the boundary so the intended segment (not the previous one) wins
    el.currentTime = Math.max(0, seconds + 0.005);
    setTime(el.currentTime);
    if (autoplay && el.paused) void el.play().catch(() => undefined);
  }, []);

  const togglePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) void el.play().catch(() => undefined);
    else el.pause();
  }, []);

  const onExport = useCallback(() => {
    downloadJson(buildDatasetFrom(task, segments), datasetFileName(task));
  }, [task, segments]);

  /**
   * Which clip the lane centres on during karaoke.
   *
   * The active index goes to -1 every time the playhead crosses a gap between
   * words, and letting the window snap back to the start of the file on each of
   * those would make the lane unreadable. So it holds the last clip that was
   * really playing.
   */
  const lastActiveId = useRef<string | null>(null);
  if (activeIndex >= 0 && segments[activeIndex]) lastActiveId.current = segments[activeIndex].id;
  const laneSegmentId =
    (activeIndex >= 0 ? segments[activeIndex]?.id : lastActiveId.current) ?? segments[0]?.id ?? null;

  const transcript = useMemo(() => transcriptOf(segments), [segments]);
  const counts = useMemo(() => countKinds(segments), [segments]);
  const correctedCount = useMemo(() => segments.filter(wasCorrected).length, [segments]);

  return (
    <>
      <header className="page__head">
        <h1 className="page__title lf-title-truncate">{task.name}</h1>
        <Button size="sm" variant="ghost" onClick={onBack}>
          ‹ Back
        </Button>
      </header>

      {error && <p className="lf-error">{error}</p>}

      {audioUrl && (
        <audio
          ref={setAudioNode}
          src={audioUrl}
          preload="metadata"
          onPlay={() => {
            setIsPlaying(true);
            startLoop();
          }}
          onPause={() => {
            setIsPlaying(false);
            stopLoop();
          }}
          onEnded={() => {
            setIsPlaying(false);
            stopLoop();
          }}
          onSeeked={(e) => setTime(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            if (Number.isFinite(d) && d > 0) setDuration(d);
          }}
        />
      )}

      <div className="card lf-player">
        <div className="row">
          <Button variant="primary" onClick={togglePlay} disabled={!audioUrl} aria-label={isPlaying ? 'Pause' : 'Play'}>
            {isPlaying ? '❚❚ Pause' : '▶ Play'}
          </Button>
          <Button size="sm" onClick={() => seekTo(0, false)} disabled={!audioUrl}>
            ⟲ Restart
          </Button>
          <span className="spacer" />
          <span className="mono lf-clock">
            {formatTime(time)} / {formatTime(duration)}
          </span>
        </div>

        <input
          className="lf-scrub"
          type="range"
          min={0}
          max={Math.max(duration, 0.01)}
          step={0.01}
          value={Math.min(time, duration || 0)}
          onChange={(e) => seekTo(Number(e.target.value), false)}
          disabled={!audioUrl}
          aria-label="Seek"
        />
      </div>

      {/* Read-only here: a completed task's cuts are settled, and the lane is
          worth the reuse purely as a picture of where the words sit. */}
      {!loading && segments.length > 0 && audioUrl && (
        <WaveLane
          taskId={task.id}
          currentSegmentId={laneSegmentId}
          durationSec={duration || task.durationSec}
          playheads={[{ kind: 'file', ref: audioRef, active: isPlaying }]}
        />
      )}

      {loading ? (
        <p className="lf-muted">Loading transcript…</p>
      ) : segments.length === 0 ? (
        <EmptyState icon="📄" title="No segments" hint="This task has no stored segments." />
      ) : (
        <>
          <div className="lf-section-head">
            <span>Transcript</span>
            <span className="mono lf-meta">
              {activeIndex >= 0 ? `${activeIndex + 1} / ${segments.length}` : `${segments.length} segments`}
            </span>
          </div>

          <div className="lf-chips" ref={listRef}>
            {segments.map((s, i) => {
              const kind = finalKind(s);
              const isActive = i === activeIndex;
              const isPast = i < passedIndex || (i === passedIndex && !isActive && time >= s.endSec);
              return (
                <button
                  key={s.id}
                  ref={isActive ? activeRef : undefined}
                  type="button"
                  className={[
                    'lf-chip',
                    `lf-chip--${kind}`,
                    isActive ? 'is-active' : '',
                    isPast ? 'is-past' : '',
                    wasCorrected(s) ? 'is-corrected' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => seekTo(s.startSec, true)}
                  title={`${formatTime(s.startSec)} – ${formatTime(s.endSec)} · ${kind}${
                    wasCorrected(s) ? ' · corrected' : ''
                  }`}
                >
                  {displayLabel(s)}
                </button>
              );
            })}
          </div>

          <div className="lf-section-head">
            <span>Summary</span>
          </div>
          <div className="card">
            <div className="row lf-badges">
              {(['word', 'filler', 'noise', 'unclear'] as SegmentKind[])
                .filter((k) => counts[k] > 0)
                .map((k) => (
                  <span key={k} className="row lf-badge-row">
                    <KindBadge kind={k} />
                    <span className="mono lf-meta">{counts[k]}</span>
                  </span>
                ))}
            </div>
            <p className="lf-meta lf-summary-line">
              {segments.length} segments · {correctedCount} corrected · {formatTime(task.durationSec)} audio
            </p>
            <p className="lf-plain">{transcript || '—'}</p>
          </div>

          <div className="lf-actions">
            <Button variant="primary" block onClick={onExport}>
              ⤓ Export JSON
            </Button>
          </div>
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Index of the last segment whose start time has passed, or -1. */
function findLastStartedIndex(segments: Segment[], t: number): number {
  let lo = 0;
  let hi = segments.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (segments[mid].startSec <= t) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

/** Index of the segment containing `t`, or -1 when the playhead is in a gap. */
function findActiveIndex(segments: Segment[], t: number): number {
  const i = findLastStartedIndex(segments, t);
  if (i < 0) return -1;
  return t < segments[i].endSec ? i : -1;
}

/** What to render inside a chip — noise/unclear get bracketed placeholders. */
function displayLabel(s: Segment): string {
  const text = finalWord(s).trim();
  const kind = finalKind(s);
  if (kind === 'noise') {
    // the Correct tab's "Background noise" option already stores "[noise]" —
    // wrapping unconditionally rendered that as "[[noise]]"
    if (text.startsWith('[') && text.endsWith(']')) return text;
    return `[${text || 'noise'}]`;
  }
  if (kind === 'unclear') return text || '???';
  return text || '·';
}

function countKinds(segments: Segment[]): Record<SegmentKind, number> {
  const counts: Record<SegmentKind, number> = { word: 0, filler: 0, noise: 0, unclear: 0, silence: 0 };
  for (const s of segments) counts[finalKind(s)]++;
  return counts;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatDate(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
